// Szolgáltatás-hozzárendelések — a SZOLGÁLTATÁSOK lap „Szolgáltatások" FÜLE (`routes/services.tsx` a héj;
// a fül nem önálló route). A KÖZÖS DataTable-re épül (az invoices a
// referencia-minta): szerver-oldali (BFF) kereső (300 ms debounce) + ügyfél/fiók/típus/státusz/gyakoriság-szűrő +
// darabszám-meta, szerver-rendezés (serviceName / clientName / netAmount), végtelen görgetés (100/lap). A
// /service-assignments BFF-végpontról fogyaszt (CSAK root; ügyfél/fiók + szolgáltatás/plan név feloldva, számított
// nettó alap-összeg). Page-gate: `service_assignments.view`.
//
// Írás-vertikum: create/edit FormSheet (service_assignments.manage) + lezárás (close, billing+recurring) + törlés
// (service_assignments.delete; számlázottnál a BFF 409 → toast + rollback).
import { type ServiceAssignmentRowSchema } from '@atlas/contracts';
import { PlusIcon } from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { ServiceAssignmentAttentionDialog } from '@/components/service-assignments/service-assignment-attention-dialog';
import { ServiceAssignmentCloneDialog } from '@/components/service-assignments/service-assignment-clone-dialog';
import { ServiceAssignmentCloseDialog } from '@/components/service-assignments/service-assignment-close-dialog';
import { ServiceAssignmentFormSheet } from '@/components/service-assignments/service-assignment-form-sheet';
import {
  isAssignmentClosed,
  ServiceAssignmentRowActions,
} from '@/components/service-assignments/service-assignment-row-actions';
import { PendingStartBadge } from '@/components/service-assignments/pending-start-badge';
import { ServiceStatusBadge } from '@/components/service-assignments/service-status-badge';
import { ServiceTypeBadge } from '@/components/service-assignments/service-type-badge';
import { AttentionBanner } from '@/components/shared/attention-banner';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { ScopeBadge } from '@/components/shared/scope-badge';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { serviceAssignmentKeys } from '@/features/service-assignments/api';
import {
  SERVICE_ASSIGNMENT_BILLING_AGREEMENTS,
  SERVICE_ASSIGNMENT_BILLING_FREQUENCIES,
  SERVICE_ASSIGNMENT_SCOPES,
  SERVICE_ASSIGNMENT_STATUSES,
  SERVICE_ASSIGNMENT_TYPES,
} from '@/features/service-assignments/form';
import { priceRowLabel, VISIBLE_PRICE_ROWS } from '@/features/service-catalog/price-rows';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { useListFilters } from '@/hooks/use-list-filters';
import { assignmentSubLines } from '@/lib/service-plan-label';
import type { TabView } from '@/lib/tab-view';
import { api } from '@/lib/api';
import { DEFAULT_TIME_ZONE, formatDateTime } from '@/lib/format-datetime';
import { formatMoney } from '@/lib/format-money';
import { PRICING_TYPES } from '@/lib/pricing-form';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

type ServiceAssignmentRow = z.infer<typeof ServiceAssignmentRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.serviceAssignments.query>>;

const PAGE_SIZE = 100;
const ALL = '__all__';

export interface ServiceAssignmentsViewProps {
  /** A fül AKTÍV-e: rejtett fülön a lekérdezés el sem indul (nem terheli az Odin kérés-plafonját). */
  enabled: boolean;
  /** Deep-link: a `?edit=` paraméterből érkező hozzárendelés-id (a szerkesztő-sheet erre nyílik, ha a sor betöltött). */
  deepLinkEditId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
  /** Deep-link: az ügyfél-szűrő KEZDŐ értéke (`?clientId=`). A felhasználó utána szabadon átállíthatja. */
  initialClientId?: string | undefined;
  /** Sor-kiemelés (`?highlight=`) — a kihagyás-napló sor-akciója és az értesítés-mélylink adja. */
  highlightId?: string | undefined;
}

export function useServiceAssignmentsView({
  enabled,
  deepLinkEditId,
  onClearDeepLink,
  initialClientId,
  highlightId,
}: ServiceAssignmentsViewProps): TabView {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('service_assignments.view');
  const canManage = can('service_assignments.manage');
  const canDelete = can('service_assignments.delete');

  // FIGYELEM-PANEL: hamarosan induló, még FÜGGŐ hozzárendelések (ugyanaz a szabály, mint a napi cronon,
  // `service_assignment.pending_start_soon`). Jog-kapu: ugyanaz, mint a listáé.
  const attentionQ = api.serviceAssignmentAttention.useQuery(
    serviceAssignmentKeys.attention(),
    {},
    { enabled: canView, queryKey: serviceAssignmentKeys.attention() },
  );
  const attention = attentionQ.data?.status === 200 ? attentionQ.data.body : null;
  // `useMemo`-val EGYBEN: a `?? []` minden renderen ÚJ tömb-referenciát adna, ha a `pendingStartingSoon`-t
  // önmagában (a hookon kívül) számolnánk — az visszahurkolna a `pendingStartMap`/`openEditFromAttention`
  // deps-listájára (a lint erre figyelmeztetett).
  const pendingStartingSoon = useMemo(() => attention?.pendingStartingSoon ?? [], [attention]);
  const pendingStartMap = useMemo(
    () => new Map(pendingStartingSoon.map((p) => [p.id, p])),
    [pendingStartingSoon],
  );
  const [attentionDialogOpen, setAttentionDialogOpen] = useState(false);
  // A sor-szerkesztés a MEGLÉVŐ `?edit=` deep-linken át nyílik (lásd a wrapper `useDeepLinkSheet`-jét) —
  // a dialógusból csak a search-paramot állítjuk és zárjuk a dialógust.
  const openEditFromAttention = useCallback(
    (id: string) => {
      setAttentionDialogOpen(false);
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({ ...prev, asg_edit: id }),
        replace: true,
      });
    },
    [navigate],
  );

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    asg_q: '',
    asg_account: ALL,
    asg_scope: ALL,
    asg_type: ALL,
    asg_status: ALL,
    asg_frequency: ALL,
    asg_agreement: ALL,
    asg_pricing: ALL,
    // ALAPÉRTELMEZETT RENDEZÉS: FELVÉTEL szerint CSÖKKENŐ (a legfrissebb sor elöl) — user-döntés.
    // MIÉRT nem a szolgáltatás-név: a lista napi használata az „mit vettem fel most?" kérdés (új
    // hozzárendelés, másolás, javítás ellenőrzése), és betűrendben a friss sor a lista közepén landolt,
    // ezer sor között. A név szerinti rendezés egy kattintás maradt a fejlécen.
    asg_sort: 'createdAt',
    asg_order: 'desc',
  });
  const q = urlFilters.asg_q;
  const accountId = urlFilters.asg_account;
  const scope = urlFilters.asg_scope;
  const type = urlFilters.asg_type;
  const status = urlFilters.asg_status;
  const billingFrequency = urlFilters.asg_frequency;
  const billingAgreement = urlFilters.asg_agreement;
  const pricingType = urlFilters.asg_pricing;
  const sort = urlFilters.asg_sort;
  const order = urlFilters.asg_order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  // A deep-link ÜGYFÉL-SZŰKÍTÉSE (az ügyfél-detail „Szolgáltatások" belépőjéből, `?clientId=`). A felület
  // ügyfél-LEGÖRDÜLŐJE kivezetve (a keresőre bízzuk), ezért ez már nem állítható a felhasználó
  // által: a belépő-érték a lekérdezés-szűrő, és a szűrő-törlés (`Szűrők törlése`) sem nyúl hozzá — a
  // linkelt nézet így ügyfél-szűkítve marad, ahogy a hívó szánta.
  const clientId = initialClientId ?? ALL;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ServiceAssignmentRow | null>(null);
  /** A MÁSOLÁS forrása (a cél-választó modál sora) — null → a modál zárva. */
  const [cloneTarget, setCloneTarget] = useState<ServiceAssignmentRow | null>(null);
  const [closeTarget, setCloseTarget] = useState<ServiceAssignmentRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ServiceAssignmentRow | null>(null);

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ asg_q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  const gated = !permsLoading && !canView;

  // A REFS-lekérdezés innen KIVEZETVE: egyedül az ügyfél-/fiók-szűrő legördülőit töltötte, azok
  // pedig megszűntek (a keresőre bízzuk). A SZERKESZTŐ-sheet a saját refs-lekérdezését futtatja (ugyanaz a
  // query-kulcs, tehát a cache közös) — a lapon így nem marad holt hálózati hívás.
  const filters = {
    q,
    clientId,
    accountId,
    scope,
    type,
    status,
    billingFrequency,
    billingAgreement,
    pricingType,
    sort,
    order,
  };
  const listKey = serviceAssignmentKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: enabled && !gated,
    queryFn: ({ pageParam }) =>
      api.serviceAssignments.query({
        query: {
          q: q || undefined,
          clientId: clientId === ALL ? undefined : clientId,
          accountId: accountId === ALL ? undefined : accountId,
          scope: scope === ALL ? undefined : (scope as 'client' | 'account'),
          type: type === ALL ? undefined : (type as 'tracking' | 'billing'),
          status: status === ALL ? undefined : (status as 'active' | 'pending' | 'inactive'),
          billingFrequency:
            billingFrequency === ALL
              ? undefined
              : (billingFrequency as (typeof SERVICE_ASSIGNMENT_BILLING_FREQUENCIES)[number]),
          billingAgreement:
            billingAgreement === ALL ? undefined : (billingAgreement as 'prepaid' | 'postpaid'),
          pricingType:
            pricingType === ALL
              ? undefined
              : (pricingType as 'flat' | 'tiered' | 'base_tiered' | 'commission'),
          sort,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.assignments : [])),
    [pages],
  );

  // Deep-link: a `?edit=<id>` hozzárendelésre nyitjuk a szerkesztő-sheetet, amint a sor betöltött (különben graceful no-op).
  useDeepLinkSheet<ServiceAssignmentRow>({
    id: deepLinkEditId,
    rows,
    getId: (a) => a.id,
    onOpen: (a) => setEditTarget(a),
  });

  // SOR-KIEMELÉS (`?highlight=`): a kihagyás-napló sor-akciója és az értesítés-mélylink navigál ide. A sor
  // ACCENT-et kap (lásd `rowClassName` lent), és EGYSZER görgetünk hozzá (a `data-row-id` a `DataTable` stabil
  // sor-azonosítója) — nem minden render, csak amikor a sor ELŐSZÖR betöltött.
  const highlightScrolled = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!highlightId || highlightScrolled.current === highlightId) return;
    if (!rows.some((a) => a.id === highlightId)) return;
    const el = document.querySelector(`[data-row-id="${highlightId}"]`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      highlightScrolled.current = highlightId;
    }
  }, [highlightId, rows]);

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters =
    q !== '' ||
    clientId !== ALL ||
    accountId !== ALL ||
    type !== ALL ||
    status !== ALL ||
    billingFrequency !== ALL;

  // Oszlop-fejléc kattintás: ugyanaz az oszlop → irány-váltás; MÁS oszlop → az új oszlop, `asc`-ből.
  const toggleSort = useCallback(
    (col: string) => {
      setFilters(
        col === sort
          ? { asg_order: order === 'asc' ? 'desc' : 'asc' }
          : { asg_sort: col, asg_order: 'asc' },
      );
    },
    [sort, order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista sor-eltávolítás + rollback): számlázottnál a BFF 409 → conflict-toast (csak lezárható).
  // A TÖRLÉS IS OCC-s: a szerver a sor verzióját várja `If-Match`-ben. Enélkül 428 — és nem véletlenül: a
  // szerver-oldali guardok (elsődleges profil · használatban lévő kártya · számlázott hozzárendelés) az
  // OLVASOTT állapotra szólnak, a törlés viszont később megy; a kettő között beérkező módosítás enélkül
  // elveszett volna.
  const removeMutation = useEntityMutation<{ id: string; version: number }, InfiniteData<ListPage>>(
    {
      mutationFn: ({ id, version }) =>
        api.serviceAssignmentDelete.mutation({
          params: { id },
          headers: { 'if-match': String(version) },
        }),
      invalidateKeys: [serviceAssignmentKeys.lists()],
      optimistic: {
        queryKey: listKey,
        update: (old) => {
          const id = deleteTarget?.id;
          if (!old || !id) return old;
          return {
            ...old,
            pages: old.pages.map((p) =>
              p.status === 200
                ? {
                    ...p,
                    body: {
                      ...p.body,
                      assignments: p.body.assignments.filter((a) => a.id !== id),
                      total: Math.max(0, p.body.total - 1),
                    },
                  }
                : p,
            ),
          };
        },
      },
      messages: {
        success: t('toast:serviceAssignments.deleted'),
        conflict: t('toast:serviceAssignments.deleteInvoiced'),
        forbidden: t('toast:serviceAssignments.forbidden'),
        validation: t('toast:serviceAssignments.validation'),
        error: t('toast:serviceAssignments.error'),
      },
    },
  );

  // Lezárás (billingEndDate beállítás) — a BFF a nem billing+recurring esetet 422-zi. If-Match a sor version-jéből.
  const closeMutation = useEntityMutation<{ id: string; version: number; billingEndDate: string }>({
    mutationFn: ({ id, version, billingEndDate }) =>
      api.serviceAssignmentClose.mutation({
        params: { id },
        body: { billingEndDate },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [serviceAssignmentKeys.lists()],
    messages: {
      success: t('toast:serviceAssignments.closed'),
      conflict: t('toast:serviceAssignments.conflict'),
      forbidden: t('toast:serviceAssignments.forbidden'),
      validation: t('toast:serviceAssignments.validation'),
      error: t('toast:serviceAssignments.error'),
    },
  });

  /**
   * FELOLDÁS — ugyanaz a végpont, mint a lezárásé (`billingEndDate: null` = a záró dátumok törlése), de
   * KÜLÖN mutáció a SAJÁT siker-üzenetéért: egy közös toast „lezárva"-t mondana feloldáskor.
   */
  const reopenMutation = useEntityMutation<{ id: string; version: number }>({
    mutationFn: ({ id, version }) =>
      api.serviceAssignmentClose.mutation({
        params: { id },
        body: { billingEndDate: null },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [serviceAssignmentKeys.lists()],
    messages: {
      success: t('serviceAssignments.reopen.success'),
      conflict: t('toast:serviceAssignments.conflict'),
      forbidden: t('toast:serviceAssignments.forbidden'),
      validation: t('toast:serviceAssignments.validation'),
      error: t('toast:serviceAssignments.error'),
    },
  });

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate(
      { id: deleteTarget.id, version: deleteTarget.version },
      { onSettled: () => setDeleteTarget(null) },
    );
  }
  function confirmClose(billingEndDate: string): void {
    if (!closeTarget) return;
    closeMutation.mutate(
      { id: closeTarget.id, version: closeTarget.version, billingEndDate },
      { onSuccess: () => setCloseTarget(null) },
    );
  }
  /**
   * FELOLDÁS: a záró dátumok törlése (`billingEndDate: null`) — UGYANAZ a végpont, mint a lezárásé, csak
   * a másik irányban. Nincs külön megerősítő ablak: a művelet visszafordítható (egy kattintás a lakat),
   * és nem jár adat-vesztéssel.
   */
  function reopen(row: ServiceAssignmentRow): void {
    reopenMutation.mutate({ id: row.id, version: row.version });
  }

  const emDash = <span className="text-muted-foreground">—</span>;
  const locale = i18n.language;

  const columns: DataTableColumn<ServiceAssignmentRow>[] = [
    {
      // 1. Szolgáltatás — a szolgáltatás neve (itemTitle, rendezhető) + a konstrukció (plan) halvány másodlagos sor.
      id: 'serviceName',
      header: t('serviceAssignments.columns.service'),
      sortable: true,
      cardLabel: t('serviceAssignments.columns.service'),
      width: 'minmax(0,1.6fr)',
      multiline: true,
      cell: (a) => (
        <div className="min-w-0 space-y-0.5">
          <Text variant="itemTitle" as="div" className="truncate">
            {a.serviceName?.trim() ? a.serviceName : emDash}
          </Text>
          {/* A MÁSODLAGOS AZONOSÍTÓ-SOROK a KÖZÖS `assignmentSubLines`-ból: a csomag (csak ha ELTÉR a
              szolgáltatás nevétől — egyszerű szerkezetnél a kettő azonos, a duplázás zaj), a KOMPLEXITÁS
              (nem dísz: ilyenkor az „Árazás” a komponensek összege, nem önálló ár), és a HASZNÁLAT FORRÁSA
              (ugyanaz a jutalékos sor jelenthet direkt foglalást, ajánlatot vagy mindkettőt).

              MIÉRT KÖZÖS: ugyanezt a három sort írja ki az ügyfél- és a fiók-detail „Szolgáltatások”
              szekciója is — korábban ott sokkal szűkebb nevek álltak, és a kezelő nem tudta, ugyanarról a
              hozzárendelésről van-e szó (user-lelet). */}
          {assignmentSubLines(a, t).map((line) => (
            <Text key={line} tone="tertiary" as="div" className="truncate" title={line}>
              {line}
            </Text>
          ))}
          {/* TÍPUS-badge (számlázás / nyilvántartás) — a saját oszlopa kivezetve, de a jelzés IDE kell
              (user-döntés): a NYILVÁNTARTÁS-sor nem számlázódik, ezért nincs gyakorisága. A típus nélkül az
              üres cella megmagyarázhatatlan lenne. (Ára viszont LEHET — azt rögzíteni kell tudni, csak soha
              nem kerül számlára.) */}
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            <ServiceTypeBadge
              type={a.type}
              label={t(`serviceAssignments.typeLabel.${a.type}`, { defaultValue: a.type })}
            />
            {/* LEZÁRT jelölő. A lezárás eddig CSAK a sor-akció lakatján látszott, a sor maga ugyanúgy
                nézett ki, mint egy élő (user-lelet: „azt sem tudom, sikeresen lezárja-e, és a záró dátum
                befrissül-e"). Élesben 591 lezárt sor van, ebből 385 NYILVÁNTARTÁS-típusú — azok mind
                élőnek látszottak. A jelölő forrása a `billingEndDate` (l. `isAssignmentClosed`). */}
            {isAssignmentClosed(a) && (
              <DomainBadge
                kind="assignmentClosed"
                value="closed"
                // A ZÁRÓ DÁTUM a jelvényen van, nem külön oszlopban — a user kérdése szó szerint az volt,
                // hogy „sikeresen lezárja-e, és a záró dátum befrissül-e": a jelvény MINDKETTŐRE felel.
                label={`${t('serviceAssignments.closedBadge')} · ${formatDateTime(
                  a.billingEndDate,
                  locale,
                  DEFAULT_TIME_ZONE,
                  { dateStyle: 'short' },
                )}`}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      // 2. Ügyfél / fiók — a scope szerint EGY név + alatta a hatókör BADGE-e (ikonosan).
      //
      // A user-döntés: „ha fiók szintű, akkor csak a fiókot kell kiírni, alatta pedig badge kis
      // fiók-ikonnal; ügyfél esetén pont fordítva." A MÁSIK nevet tehát NEM írjuk ki (fiók-szintű sornál a
      // ügyfél-nevet sem): a fiók egyértelműen azonosít, és a sor így két rövid sorból áll, nem négyből.
      // Ügyfél-szintűnél a KIJELÖLT cél-fiókok listája marad (az legördülő, és valódi adat: üres lista =
      // az ügyfél minden fiókja).
      id: 'clientName',
      card: 'subtitle',
      header: t('serviceAssignments.columns.owner'),
      sortable: true,
      cardLabel: t('serviceAssignments.columns.owner'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      cell: (a) => {
        const isAccount = a.scope === 'account';
        const primary = isAccount ? a.accountName : a.clientName;
        return (
          <div className="min-w-0 space-y-1">
            <div className="truncate">{primary ?? emDash}</div>
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              <ScopeBadge
                scope={a.scope}
                label={t(`serviceAssignments.scopeLabel.${a.scope}`, { defaultValue: a.scope })}
              />
            </div>
            {/* Ügyfél-szintűnél a KIJELÖLT cél-fiókok (a legacy legördülője) — üres lista = az ügyfél minden fiókja. */}
            {a.targetAccounts.length > 0 && (
              <ExpandableCell
                items={a.targetAccounts}
                getKey={(x) => x.id}
                visible={1}
                threshold={1}
                moreLabel={(n) => t('serviceAssignments.moreTargets', { count: n })}
                triggerAriaLabel={t('serviceAssignments.moreTargetsAria')}
                renderItem={(x) => (
                  <Text tone="tertiary" as="span" className="block truncate">
                    {x.name}
                  </Text>
                )}
              />
            )}
          </div>
        );
      },
    },
    // A „Típus" (nyilvántartás/számlázás) és a „Mennyiség" oszlop KIVEZETVE (user-döntés): a legacy
    // listában sem volt önálló oszlopuk, a szerkesztőben és a SZŰRŐK között viszont maradnak — a típus valódi
    // szűrő-dimenzió, a mennyiség pedig a sor részlete, nem az azonosítója.
    {
      // 3. Gyakoriság — a számlázási ciklus ÉS a fizetési megállapodás is BADGE (user-döntés): a
      //    kettő két különböző dimenzió (mikor számlázunk / előre vagy utólag fizet), és badge-ként egy
      //    pillantással szétválik. Követés-típusú (nem számlázott) sornál nincs egyik sem → em-dash.
      id: 'billingFrequency',
      header: t('serviceAssignments.columns.frequency'),
      cardLabel: t('serviceAssignments.columns.frequency'),
      width: 'minmax(0,1.15fr)',
      multiline: true,
      cell: (a) => {
        // ESETI (`one_time`) sor: NINCS gyakorisága, mert egyszer számlázódik — ez maga a válasz a
        // kérdésre, nem hiányzó adat. Eddig em-dash állt itt (a cella a `tracking` típust is kihúzta),
        // pedig élesben 397 ilyen sor van, 396-nak VAN ársora: a lista egyszerűen elhallgatta őket.
        // A FIZETÉSI MEGÁLLAPODÁS itt NEM jelenik meg (user-lelet): eseti díjnál nincs jelentése (kézi számla), a
        // régi CRM-ből migrált sorokon mégis „utólag" állt, így az eseti sorok egy része „utólag fizetett"-nek
        // látszott, a többi nem. A mentés is kiüríti (`applyOneTimeDates`).
        if (a.billingType === 'one_time' && !a.billingFrequency) {
          return (
            <DomainBadge
              kind="frequency"
              value="one_time"
              label={t('serviceAssignments.frequencyLabel.one_time', { defaultValue: 'eseti' })}
            />
          );
        }
        // Ami tényleg hiányzik, az marad em-dash — a NYILVÁNTARTÁS-típus önmagában NEM zárja ki a
        // gyakoriságot (élesben 106 ismétlődő nyilvántartás-sor van, valódi ciklussal).
        if (!a.billingFrequency && !a.billingAgreement) return emDash;
        return (
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            {/* A jelek a KÖZÖS badge-katalógusból (`frequency` / `agreement`) — ugyanezt mutatja a bevétel-
                előrejelzés drilldownja is. Előre/utólag fizetés: az ELŐRE fizetett a kiemelt (brand-zöld — a
                pénz már megvan), az utólagos a SEMLEGES tónus; a borostyán „figyelem" a kivételnek marad. */}
            {a.billingFrequency && (
              <DomainBadge
                kind="frequency"
                value={a.billingFrequency}
                label={t(`serviceAssignments.frequencyLabel.${a.billingFrequency}`, {
                  defaultValue: a.billingFrequency,
                })}
              />
            )}
            {a.billingAgreement && (
              <DomainBadge
                kind="agreement"
                value={a.billingAgreement}
                label={t(`serviceAssignments.agreementLabel.${a.billingAgreement}`, {
                  defaultValue: a.billingAgreement,
                })}
              />
            )}
          </div>
        );
      },
    },
    {
      // 4. Árazás — az összeg + ALATTA az árazási MODELL badge-e (fix díjas / sávos / jutalékos / …).
      //
      // A legacy oszlopa is „Árazás" volt, és az összeg alatt kiírta a modellt (user-döntés: „régen
      // árazás volt, és ott volt alatta, hogy fix díjas" — most badge-ként). Ez nem kozmetika: egy SÁVOS vagy
      // JUTALÉKOS sor nettója önmagában megmagyarázhatatlan szám (a sávot a használat dönti el), a modell
      // ismerete nélkül a felhasználó rossz következtetést von le. Rendezés továbbra is az összegen.
      id: 'netAmount',
      card: 'trailing',
      header: t('serviceAssignments.columns.pricing'),
      sortable: true,
      cardLabel: t('serviceAssignments.columns.pricing'),
      width: 'minmax(0,1.45fr)',
      // BALRA igazított, mint minden oszlop (rendszer-alapértelmezés, user-döntés); az ársorok
      // `címke · ár` párjai a blokkon BELÜL futnak két élen (`justify-between`), a blokk a cella bal élén áll.
      multiline: true,
      cell: (a) => {
        // A NYILVÁNTARTÁS-sor ÁRA IS LÁTSZIK. Korábban a cella a `tracking` típust kihúzta („nem
        // számlázzuk, tehát nincs díja") — csakhogy a nyilvántartás-sornak LEHET rögzített ára, és
        // élesben 502 ilyen sor van (396 eseti + 106 ismétlődő), MIND ársorral. A legacy ezeket kiírta;
        // az em-dash itt nem információ volt, hanem adat-vesztés a képernyőn (user-lelet).
        // Ami tényleg nincs (se összeg, se ársor, se modell), az továbbra is em-dash.
        // A SÁVOS/JUTALÉKOS sor összege `null` (a `netAmount` csak az `unit`/`base` sorokból áll össze) — ott az
        // ÁRSOROKAT írjuk ki (`címke · ár`), ahogy a katalógus árazás-cellája és a legacy is tette.
        const tierRows = a.prices.filter((pr) => pr.type !== 'unit' && pr.type !== 'base');
        const showTiers = a.netAmount === null && tierRows.length > 0;
        if (a.netAmount === null && !showTiers && a.pricingType === null) return emDash;
        return (
          <div className="flex min-w-0 max-w-[13rem] flex-col items-start gap-1">
            {a.netAmount !== null && (
              <span className="whitespace-nowrap tabular-nums">
                {formatMoney(a.netAmount, a.currency ?? 'HUF', locale)}
              </span>
            )}
            {showTiers && (
              <div className="w-full space-y-0.5">
                {tierRows.slice(0, VISIBLE_PRICE_ROWS).map((pr, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-2">
                    <span className="text-muted-foreground min-w-0 truncate text-xs">
                      {priceRowLabel(pr, t)}
                    </span>
                    <span className="shrink-0 whitespace-nowrap tabular-nums">
                      {formatMoney(pr.netPrice, pr.currency, locale)}
                    </span>
                  </div>
                ))}
                {tierRows.length > VISIBLE_PRICE_ROWS && (
                  <Text tone="tertiary" as="div" className="text-xs">
                    {t('serviceCatalog.morePrices', {
                      count: tierRows.length - VISIBLE_PRICE_ROWS,
                    })}
                  </Text>
                )}
              </div>
            )}
            {(a.pricingType !== null || a.components.length > 0) && (
              <div className="flex min-w-0 flex-wrap items-center gap-1">
                {a.pricingType && (
                  <DomainBadge
                    kind="pricingType"
                    value={a.pricingType}
                    label={t(`serviceCatalog.pricingTypeLabel.${a.pricingType}`, {
                      defaultValue: a.pricingType,
                    })}
                  />
                )}
                {/* KOMPLEX sor: a komponensek legördülőben (név · ár) — a legacy lista is így mutatta. A cella
                    többi része változatlan; a lenyitás csak megmutatja, miből áll össze az összeg. */}
                {a.components.length > 0 && (
                  <ExpandableCell
                    items={a.components}
                    getKey={(c) => c.id}
                    visible={0}
                    threshold={0}
                    moreLabel={(n) => t('serviceAssignments.componentsCount', { count: n })}
                    triggerAriaLabel={t('serviceAssignments.componentsAria')}
                    triggerClassName="w-auto px-2"
                    contentClassName="min-w-[16rem] max-w-[min(22rem,calc(100vw-2rem))]"
                    renderItem={(c) => (
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 break-words">{c.name ?? '—'}</span>
                        <span className="text-foreground shrink-0 whitespace-nowrap tabular-nums">
                          {c.unitPrice === null ? (
                            '—'
                          ) : (
                            <>
                              {/* a mennyiség csak akkor, ha nem 1 — a megjelenítéshez, nem számoláshoz */}
                              {Number(c.quantity) !== 1 && (
                                <span className="text-muted-foreground">{c.quantity} × </span>
                              )}
                              {formatMoney(c.unitPrice, c.currency ?? a.currency ?? 'HUF', locale)}
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  />
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      // 5. Státusz — active / pending / inactive badge (utolsó, dedikált oszlop) + FÜGGŐ sornál az indulás-
      //    jelölő (`PendingStartBadge`): a számlázás kezdete mindig látszik, a „hamarosan"/„elmaradt" tónust a
      //    figyelő halmaza adja (a figyelem-panel ugyanazt a listát mutatja).
      id: 'status',
      header: t('serviceAssignments.columns.status'),
      cardLabel: t('serviceAssignments.columns.status'),
      // a függő sor indulás-jelölője dátummal („indulás elmaradt: éééé. hh. nn.") ~180 px — 1.1fr-en 1280-on
      // 115 px jutott, és a dátum levágódott (mérve); a szélesség a két ELLIPSZISSEL rövidülő szöveg-oszlopból jön
      width: 'minmax(0,2.15fr)',
      multiline: true,
      // KÁRTYA (<lg): a fejléc státusz-helye nem zsugorodik — a függő sor jelvénye a státusz ALÁ kerül, évszám
      // nélküli dátummal, különben a címet teljesen kiszorította (375 px-en mérve)
      cardCell: (a) => (
        <div className="flex flex-col items-end gap-1">
          <ServiceStatusBadge
            status={a.status}
            label={t(`serviceAssignments.statusLabel.${a.status}`, { defaultValue: a.status })}
          />
          {a.status === 'pending' && (
            <PendingStartBadge
              billingStartDate={a.billingStartDate}
              attention={pendingStartMap.get(a.id)}
              compact
            />
          )}
        </div>
      ),
      cell: (a) => {
        const pending = pendingStartMap.get(a.id);
        return (
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            <ServiceStatusBadge
              status={a.status}
              label={t(`serviceAssignments.statusLabel.${a.status}`, { defaultValue: a.status })}
            />
            {a.status === 'pending' && (
              <PendingStartBadge billingStartDate={a.billingStartDate} attention={pending} />
            )}
          </div>
        );
      },
    },
  ];

  // A FÜL NÉZETE — a lap-héj (`routes/services.tsx`) rendereli az EGYETLEN `AppShell`-be.
  return {
    ...(canManage
      ? {
          primaryAction: {
            icon: <PlusIcon className="size-5 stroke-2" />,
            label: t('serviceAssignments.new'),
            onClick: () => setCreateOpen(true),
          },
        }
      : {}),
    // A SÁV-BLOKK NEM HORD SAJÁT TÉRKÖZT (`gap`): a figyelem-panel a saját `pt-2`-jével ül a helyén, a
    // toolbar pedig a sajátjával — egy extra `gap-2` itt NAGYOBB rést adott a panel és a kereső közé, mint
    // amekkora a kereső és a lista közt van, és a sáv optikailag kettéesett.
    toolbar: (
      <div className="flex flex-col">
        {/* FIGYELEM-PANEL a toolbar FÖLÖTT (user-döntés a design.md CC7 lista-konvenciója mellett): csak
              akkor renderel, ha van hamarosan induló, függő hozzárendelés — az üres állapot nem foglal helyet. */}
        {pendingStartingSoon.length > 0 && (
          <div className="px-4 pt-2">
            <AttentionBanner
              tone="warning"
              title={t('serviceAssignments.attention.bannerTitle', {
                count: pendingStartingSoon.length,
                noticeDays: attention?.noticeDays ?? 0,
              })}
              action={
                <Button variant="outline" size="sm" onClick={() => setAttentionDialogOpen(true)}>
                  {t('serviceAssignments.attention.reviewButton')}
                </Button>
              }
            />
          </div>
        )}
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
          }}
          // A SZŰRŐ-SORREND (user-döntés): HATÓKÖR → típus → gyakoriság → fizetés → árazás →
          // STÁTUSZ. A státusz MINDIG az utolsó (a státusz-oszlop is az), a hatókör pedig az ELSŐ: az
          // „ügyfél-szintű vagy fiók-szintű" a lista legfontosabb kettős-bontása.
          //
          // KIVEZETVE: az ÜGYFÉL- és a FIÓK-szűrő. A konkrét ügyfélre/fiókra a KERESŐ szűr (a BFF a névre is
          // keres), és a két hosszú, több százas legördülő a popover felét vitte el. A `clientId` search-param
          // (az ügyfél-detail belépője) VÁLTOZATLANUL működik — csak a felület nem kínál rá legördülőt.
          filters={[
            {
              key: 'scope',
              label: t('serviceAssignments.filterScope'),
              value: scope,
              onChange: (v: string) => setFilters({ asg_scope: v }),
              options: SERVICE_ASSIGNMENT_SCOPES.map((v) => ({
                value: v,
                label: t(`serviceAssignments.scopeLabel.${v}`, { defaultValue: v }),
              })),
              allValue: ALL,
            },
            {
              key: 'type',
              label: t('serviceAssignments.filterType'),
              value: type,
              onChange: (v: string) => setFilters({ asg_type: v }),
              options: SERVICE_ASSIGNMENT_TYPES.map((v) => ({
                value: v,
                label: t(`serviceAssignments.typeLabel.${v}`, { defaultValue: v }),
              })),
              allValue: ALL,
            },
            {
              key: 'frequency',
              label: t('serviceAssignments.filterFrequency'),
              value: billingFrequency,
              onChange: (v: string) => setFilters({ asg_frequency: v }),
              options: SERVICE_ASSIGNMENT_BILLING_FREQUENCIES.map((v) => ({
                value: v,
                label: t(`serviceAssignments.frequencyLabel.${v}`, { defaultValue: v }),
              })),
              allValue: ALL,
            },
            {
              key: 'agreement',
              label: t('serviceAssignments.filterAgreement'),
              value: billingAgreement,
              onChange: (v: string) => setFilters({ asg_agreement: v }),
              options: SERVICE_ASSIGNMENT_BILLING_AGREEMENTS.map((v) => ({
                value: v,
                label: t(`serviceAssignments.agreementLabel.${v}`, { defaultValue: v }),
              })),
              allValue: ALL,
            },
            {
              key: 'pricing',
              label: t('serviceAssignments.filterPricing'),
              value: pricingType,
              onChange: (v: string) => setFilters({ asg_pricing: v }),
              options: PRICING_TYPES.map((v) => ({
                value: v,
                label: t(`serviceCatalog.pricingTypeLabel.${v}`, { defaultValue: v }),
              })),
              allValue: ALL,
            },
            {
              key: 'status',
              label: t('serviceAssignments.filterStatus'),
              value: status,
              onChange: (v: string) => setFilters({ asg_status: v }),
              options: SERVICE_ASSIGNMENT_STATUSES.map((v) => ({
                value: v,
                label: t(`serviceAssignments.statusLabel.${v}`, { defaultValue: v }),
              })),
              allValue: ALL,
            },
          ]}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('serviceAssignments.loading')
              : !isError && !infinite.isLoading
                ? t('serviceAssignments.countOf', { shown: rows.length, total })
                : ''
          }
          actions={
            <div className="hidden md:block">
              <ListNewButton
                label={t('serviceAssignments.new')}
                onClick={() => setCreateOpen(true)}
                enabled={canManage}
                disabledTooltip={t('common.noCreatePermission')}
              />
            </div>
          }
        />
      </div>
    ),
    content: (
      <>
        <div className="flex h-full flex-col px-4 pb-2">
          <DataTable<ServiceAssignmentRow>
            columns={columns}
            rows={rows}
            getRowId={(a) => a.id}
            sort={sort}
            order={order}
            onSort={toggleSort}
            // SOR-KIEMELÉS (`?highlight=`): a kihagyás-napló sor-akciója és az értesítés-mélylink navigál ide.
            rowClassName={(a) => (a.id === highlightId ? 'bg-info-soft/70' : undefined)}
            isLoading={!gated && (permsLoading || infinite.isLoading)}
            isError={isError}
            errorState={
              <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
            }
            emptyState={
              hasFilters ? (
                <EmptyState
                  title={t('serviceAssignments.noMatches')}
                  description={t('serviceAssignments.noMatchesDesc')}
                />
              ) : (
                <EmptyState
                  title={t('serviceAssignments.empty')}
                  description={t('serviceAssignments.emptyDesc')}
                />
              )
            }
            hasNextPage={infinite.hasNextPage}
            isFetchingNextPage={infinite.isFetchingNextPage}
            onLoadMore={loadMore}
            rowActionsLabel={t('serviceAssignments.columns.actions')}
            // NÉGY akció fér el (szerkesztés · másolás · lezárás VAGY feloldás · törlés) — a 30 px-es
            // ikon-gombok + a
            // köztük lévő rés miatt a korábbi 9rem már a harmadiknál szorított. A `menu` variánsú mobil
            // ⋯-listát ez nem érinti.
            actionsColWidth={canManage && canDelete ? '11rem' : canManage ? '9rem' : '5rem'}
            rowActions={(a) => (
              <ServiceAssignmentRowActions
                row={a}
                canManage={canManage}
                canDelete={canDelete}
                onClone={(row) => setCloneTarget(row)}
                onEdit={(row) => setEditTarget(row)}
                onClose={(row) => setCloseTarget(row)}
                onReopen={reopen}
                onDelete={(row) => setDeleteTarget(row)}
                t={t}
              />
            )}
          />
        </div>

        <ServiceAssignmentFormSheet
          open={createOpen}
          onOpenChange={setCreateOpen}
          assignment={null}
        />
        <ServiceAssignmentFormSheet
          open={editTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEditTarget(null);
              onClearDeepLink?.(); // deep-link: záráskor a `?edit` param törlése (back-barát + újra-nyitható)
            }
          }}
          assignment={editTarget}
        />

        <ServiceAssignmentCloneDialog
          open={cloneTarget !== null}
          onOpenChange={(open) => !open && setCloneTarget(null)}
          source={cloneTarget}
        />

        <ServiceAssignmentCloseDialog
          open={closeTarget !== null}
          onOpenChange={(open) => !open && setCloseTarget(null)}
          serviceName={closeTarget?.serviceName}
          initialEndDate={closeTarget?.billingEndDate}
          pending={closeMutation.isPending}
          onConfirm={confirmClose}
        />

        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          title={t('serviceAssignments.deleteTitle')}
          description={t('serviceAssignments.deleteDesc', {
            name: deleteTarget?.serviceName ?? '',
          })}
          confirmLabel={t('serviceAssignments.delete')}
          cancelLabel={t('serviceAssignments.cancel')}
          pendingLabel={t('serviceAssignments.deleting')}
          destructive
          pending={removeMutation.isPending}
          onConfirm={confirmDelete}
        />

        <ServiceAssignmentAttentionDialog
          open={attentionDialogOpen}
          onOpenChange={setAttentionDialogOpen}
          items={pendingStartingSoon}
          onEdit={openEditFromAttention}
        />
      </>
    ),
  };
}
